import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { CopyableField } from './CopyableField'
import { cn } from '@/lib/cn'

interface Props {
  payload: Record<string, unknown>
}

/**
 * AF-125 — Raw payload section. Always rendered as collapsible, default
 * collapsed because the JSON is bulky and analysts only open it for
 * evidence export. On print the section auto-expands (see global print CSS).
 */
export function RawPayloadPanel({ payload }: Props) {
  const [open, setOpen] = useState(false)
  const json = JSON.stringify(payload, null, 2)

  return (
    <div className="raw-payload" data-open={open}>
      {/*
        Disclosure trigger and copy widget live as siblings — the
        copy widget renders its own <button>, so nesting it inside
        the disclosure button (the previous shape) emitted React
        19's "in HTML, <button> cannot be a descendant of <button>"
        hydration warning. Splitting them keeps the same row layout
        without breaking valid-HTML invariants.
      */}
      <div className="flex w-full items-center justify-between gap-2 rounded text-sm font-medium text-fg">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="inline-flex flex-1 items-center gap-2 rounded px-2 py-2 text-left transition-colors hover:bg-muted"
        >
          {open ? (
            <ChevronDown size={14} aria-hidden />
          ) : (
            <ChevronRight size={14} aria-hidden />
          )}
          Raw payload
        </button>
        <CopyableField
          value={json}
          mono={false}
          copyLabel="Copy raw payload JSON"
          toastMessage="Raw payload copied"
          className="text-xs"
        />
      </div>
      <pre
        className={cn(
          'mt-2 max-h-80 overflow-auto rounded border border-border bg-muted/30 p-3 font-mono text-xs leading-relaxed',
          !open && 'hidden print:block',
        )}
      >
        {json}
      </pre>
    </div>
  )
}
